import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  fetchMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '../../services/notificationService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { formatDistanceToNow, format } from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGES = [
  'Pending Team Assignment',
  'Calculated',
  'Pending Approval',
  'Approved',
  'Ready for Payment',
  'Paid',
  'Rejected',
  'On Hold',
];

const STAGE_META = {
  'Pending Team Assignment': { icon: 'Users', color: 'text-warning', bg: 'bg-warning/10' },
  'Calculated': { icon: 'Calculator', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  'Pending Approval': { icon: 'Clock', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  'Approved': { icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
  'Ready for Payment': { icon: 'CreditCard', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  'Paid': { icon: 'Banknote', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  'Rejected': { icon: 'XCircle', color: 'text-error', bg: 'bg-error/10' },
  'On Hold': { icon: 'PauseCircle', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
};

const AUDIT_ACTION_META = {
  status_changed: { icon: 'RefreshCw', color: 'text-blue-400', label: 'Status Changed' },
  team_assigned: { icon: 'UserPlus', color: 'text-success', label: 'Team Assigned' },
  calculation_updated: { icon: 'Calculator', color: 'text-purple-400', label: 'Calculation Updated' },
  approved: { icon: 'CheckCircle', color: 'text-success', label: 'Approved' },
  rejected: { icon: 'XCircle', color: 'text-error', label: 'Rejected' },
  payment_processed: { icon: 'Banknote', color: 'text-emerald-400', label: 'Payment Processed' },
  comment_added: { icon: 'MessageSquare', color: 'text-muted-foreground', label: 'Comment Added' },
  created: { icon: 'Plus', color: 'text-primary', label: 'Created' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return '—';
  }
};

const formatDate = (dateStr) => {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
  } catch {
    return '—';
  }
};

// ─── Fetch audit trail (all incentive ticket audit logs) ──────────────────────

const fetchAuditTrail = async ({ limit = 100, offset = 0, ticketSearch = '' } = {}) => {
  let query = supabase
    ?.from('incentive_ticket_audit_logs')
    ?.select(`
      id,
      ticket_id,
      action,
      performed_by,
      field_changed,
      old_value,
      new_value,
      created_at,
      user_profiles!incentive_ticket_audit_logs_performed_by_fkey(full_name, email),
      incentive_tickets!incentive_ticket_audit_logs_ticket_id_fkey(ticket_number, project_name, status)
    `)
    ?.order('created_at', { ascending: false })
    ?.range(offset, offset + limit - 1);

  const { data, error } = await query;
  if (error) {
    console.warn('[AuditTrail] Fetch error:', error?.message);
    return [];
  }
  return data || [];
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const FilterBar = ({ filters, onChange, totalCount, unreadCount }) => (
  <div className="flex flex-wrap items-center gap-3 p-4 bg-card border border-border rounded-2xl">
    {/* Stage filter */}
    <div className="flex items-center gap-2 min-w-[180px]">
      <Icon name="Filter" size={14} className="text-muted-foreground flex-shrink-0" />
      <select
        value={filters?.stage}
        onChange={(e) => onChange({ ...filters, stage: e?.target?.value })}
        className="flex-1 bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <option value="">All Stages</option>
        {STAGES?.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>

    {/* Read status filter */}
    <div className="flex items-center gap-2 min-w-[160px]">
      <select
        value={filters?.readStatus}
        onChange={(e) => onChange({ ...filters, readStatus: e?.target?.value })}
        className="flex-1 bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <option value="">All Notifications</option>
        <option value="unread">Unread Only</option>
        <option value="read">Read Only</option>
      </select>
    </div>

    {/* Ticket search */}
    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
      <div className="relative flex-1">
        <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search ticket or project…"
          value={filters?.search}
          onChange={(e) => onChange({ ...filters, search: e?.target?.value })}
          className="w-full bg-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
    </div>

    {/* Stats */}
    <div className="flex items-center gap-3 ml-auto">
      <span className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{totalCount}</span> total
      </span>
      {unreadCount > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-error/15 text-error text-xs font-semibold">
          {unreadCount} unread
        </span>
      )}
    </div>
  </div>
);

const NotificationRow = ({ notification, onMarkRead, onDelete, onNavigate }) => {
  const meta = STAGE_META?.[notification?.stage] || { icon: 'Bell', color: 'text-primary', bg: 'bg-primary/10' };
  const isUnread = !notification?.is_read;

  return (
    <div
      className={`group flex items-start gap-4 p-4 rounded-2xl border transition-all duration-200 cursor-pointer hover:border-primary/30 hover:shadow-sm
        ${isUnread ? 'border-primary/20 bg-primary/5' : 'border-border bg-card'}`}
      onClick={() => onNavigate(notification)}
    >
      {/* Stage icon */}
      <div className={`flex-shrink-0 p-2.5 rounded-xl ${meta?.bg}`}>
        <Icon name={meta?.icon} size={18} className={meta?.color} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className={`text-sm leading-relaxed ${isUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              {notification?.message}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
              {notification?.ticket_number && (
                <span className="inline-flex items-center gap-1 text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-lg">
                  <Icon name="Ticket" size={10} />
                  {notification?.ticket_number}
                </span>
              )}
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg ${meta?.bg} ${meta?.color}`}>
                {notification?.stage}
              </span>
              <span className="text-xs text-muted-foreground">{timeAgo(notification?.created_at)}</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">{formatDate(notification?.created_at)}</span>
              {isUnread && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            {isUnread && (
              <button
                onClick={(e) => { e?.stopPropagation(); onMarkRead(notification?.id); }}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                title="Mark as read"
              >
                <Icon name="Check" size={14} className="text-success" />
              </button>
            )}
            <button
              onClick={(e) => { e?.stopPropagation(); onDelete(notification?.id); }}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              title="Dismiss"
            >
              <Icon name="Trash2" size={14} className="text-muted-foreground hover:text-error" />
            </button>
            {notification?.action_url && (
              <button
                onClick={(e) => { e?.stopPropagation(); onNavigate(notification); }}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                title="Go to ticket"
              >
                <Icon name="ArrowRight" size={14} className="text-primary" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AuditTrailRow = ({ log }) => {
  const actionKey = log?.action?.toLowerCase()?.replace(/\s+/g, '_') || '';
  const meta = AUDIT_ACTION_META?.[actionKey] || { icon: 'Activity', color: 'text-muted-foreground', label: log?.action };
  const performedBy = log?.user_profiles?.full_name || log?.user_profiles?.email || 'System';
  const ticket = log?.incentive_tickets;

  return (
    <div className="flex items-start gap-4 py-3 px-4 border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors rounded-xl">
      {/* Action icon */}
      <div className="flex-shrink-0 mt-0.5">
        <div className="p-2 rounded-xl bg-muted/50">
          <Icon name={meta?.icon} size={14} className={meta?.color} />
        </div>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{meta?.label}</span>
          {ticket?.ticket_number && (
            <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-lg">
              {ticket?.ticket_number}
            </span>
          )}
          {ticket?.project_name && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">{ticket?.project_name}</span>
          )}
        </div>

        {/* Field change detail */}
        {log?.field_changed && (
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium">{log?.field_changed}:</span>
            {log?.old_value && (
              <>
                <span className="line-through text-error/70">{log?.old_value}</span>
                <Icon name="ArrowRight" size={10} />
              </>
            )}
            {log?.new_value && <span className="text-success">{log?.new_value}</span>}
          </div>
        )}

        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-muted-foreground">
            by <span className="font-medium text-foreground">{performedBy}</span>
          </span>
          <span className="text-xs text-muted-foreground">{timeAgo(log?.created_at)}</span>
          <span className="text-xs text-muted-foreground hidden sm:inline">{formatDate(log?.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const NotificationHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useRBAC();

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [filters, setFilters] = useState({ stage: '', readStatus: '', search: '' });

  // Audit trail state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setNotifLoading(true);
    try {
      const data = await fetchMyNotifications({ limit: 200 });
      setNotifications(data || []);
    } catch (err) {
      console.warn('[NotifHistory] Load error:', err?.message);
    } finally {
      setNotifLoading(false);
    }
  }, [user?.id]);

  // Load audit trail
  const loadAuditTrail = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await fetchAuditTrail({ limit: 200 });
      setAuditLogs(data || []);
    } catch (err) {
      console.warn('[NotifHistory] Audit load error:', err?.message);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditTrail();
    }
  }, [activeTab, loadAuditTrail]);

  // Filter notifications
  const filteredNotifications = notifications?.filter(n => {
    if (filters?.stage && n?.stage !== filters?.stage) return false;
    if (filters?.readStatus === 'unread' && n?.is_read) return false;
    if (filters?.readStatus === 'read' && !n?.is_read) return false;
    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      if (
        !n?.ticket_number?.toLowerCase()?.includes(q) &&
        !n?.project_name?.toLowerCase()?.includes(q) &&
        !n?.message?.toLowerCase()?.includes(q)
      ) return false;
    }
    return true;
  });

  // Filter audit logs
  const filteredAuditLogs = auditLogs?.filter(log => {
    if (!auditSearch) return true;
    const q = auditSearch?.toLowerCase();
    return (
      log?.incentive_tickets?.ticket_number?.toLowerCase()?.includes(q) ||
      log?.incentive_tickets?.project_name?.toLowerCase()?.includes(q) ||
      log?.user_profiles?.full_name?.toLowerCase()?.includes(q) ||
      log?.action?.toLowerCase()?.includes(q)
    );
  });

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, is_read: true } : n));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
    setNotifications(prev => prev?.filter(n => n?.id !== id));
  };

  const handleNavigate = async (notification) => {
    if (!notification?.is_read) {
      await markNotificationRead(notification?.id);
      setNotifications(prev => prev?.map(n => n?.id === notification?.id ? { ...n, is_read: true } : n));
    }
    if (notification?.action_url) {
      navigate(notification?.action_url);
    }
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Icon name="Bell" size={20} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Notification History</h1>
            </div>
            <p className="text-sm text-muted-foreground ml-[52px]">
              All your workflow notifications and a full audit trail of ticket actions.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllRead}
                className="flex items-center gap-2"
              >
                <Icon name="CheckCheck" size={14} />
                Mark all read
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={activeTab === 'notifications' ? loadNotifications : loadAuditTrail}
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={14} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-muted rounded-2xl w-fit">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
              ${activeTab === 'notifications' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="Bell" size={15} />
            My Notifications
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-error text-white text-[10px] font-bold leading-none">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
              ${activeTab === 'audit' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="ClipboardList" size={15} />
            Audit Trail
          </button>
        </div>

        {/* ── NOTIFICATIONS TAB ── */}
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            {/* Filter bar */}
            <FilterBar
              filters={filters}
              onChange={setFilters}
              totalCount={filteredNotifications?.length}
              unreadCount={unreadCount}
            />

            {/* Notification list */}
            {notifLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredNotifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 bg-card border border-border rounded-2xl">
                <div className="p-4 rounded-full bg-muted/50">
                  <Icon name="BellOff" size={28} className="text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">No notifications found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filters?.stage || filters?.readStatus || filters?.search
                      ? 'Try adjusting your filters' :'You have no notifications yet'}
                  </p>
                </div>
                {(filters?.stage || filters?.readStatus || filters?.search) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters({ stage: '', readStatus: '', search: '' })}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredNotifications?.map(notification => (
                  <NotificationRow
                    key={notification?.id}
                    notification={notification}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT TRAIL TAB ── */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            {/* Audit search */}
            <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-2xl">
              <div className="relative flex-1">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by ticket, project, user, or action…"
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e?.target?.value)}
                  className="w-full bg-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                <span className="font-semibold text-foreground">{filteredAuditLogs?.length}</span> entries
              </span>
            </div>

            {/* Audit log list */}
            {auditLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredAuditLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 bg-card border border-border rounded-2xl">
                <div className="p-4 rounded-full bg-muted/50">
                  <Icon name="ClipboardX" size={28} className="text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">No audit entries found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {auditSearch ? 'Try a different search term' : 'No ticket actions have been recorded yet'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
                  <Icon name="ClipboardList" size={15} className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">Full Audit Trail</span>
                  <span className="text-xs text-muted-foreground">— who acted when on every ticket</span>
                </div>
                <div className="divide-y divide-border/50">
                  {filteredAuditLogs?.map(log => (
                    <AuditTrailRow key={log?.id} log={log} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default NotificationHistory;
